import { getNumberFromQueryString } from '@if/core/utils'
import BoltIcon from '@mui/icons-material/Bolt'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid'
import { enUS, frFR } from '@mui/x-data-grid/locales'
import Footer from '@src/tools/error-tracker/pages/BrowseExceptionsPage/Components/footer'
import { formatDateTime } from '@src/shell/utils/Formatter'
import {
  useClearDeadLetters,
  useDeadLettersList,
  useDiscardDeadLetters,
  useRetriggerDeadLetters,
} from '@src/tools/dead-letter/api/dead-letters-api'
import { useUser } from '@src/shared/contexts/UserContext'
import { DeadLetterFiltersDto, DeadLetterLog } from '@src/tools/dead-letter/types/Constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import DeadLetterDetailDialog from './Components/DeadLetterDetailDialog'
import SubscriptionSidebar from './Components/SubscriptionSidebar'

const DeadLettersPage = () => {
  const { i18n, t } = useTranslation()
  const { user } = useUser()
  const canClear = !!user?.rights.clearDeadLetter

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const subscriptionName = searchParams.get('subscriptionName') || ''
  const isSubscriptionSelected = !!subscriptionName

  // Dialog state
  const [selectedDeadLetter, setSelectedDeadLetter] = useState<DeadLetterLog | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([])
  const [retriggerSuccess, setRetriggerSuccess] = useState<string | null>(null)
  const [discardSuccess, setDiscardSuccess] = useState<string | null>(null)
  const [confirmRetrigger, setConfirmRetrigger] = useState<{ type: 'single'; deadLetter: DeadLetterLog } | { type: 'bulk' } | null>(null)
  const [confirmDiscard, setConfirmDiscard] = useState<boolean>(false)
  const [confirmClear, setConfirmClear] = useState<boolean>(false)
  const [clearSuccess, setClearSuccess] = useState<string | null>(null)

  // Pagination
  const limit = getNumberFromQueryString(searchParams, 'limit', 25)
  const offset = getNumberFromQueryString(searchParams, 'offset', 0)
  const pagination: GridPaginationModel = { pageSize: limit, page: offset / limit }

  const filters: DeadLetterFiltersDto = useMemo(
    () => ({
      subscriptionName,
      limit,
      offset,
    }),
    [subscriptionName, limit, offset],
  )

  // Dead letters for selected subscription
  const [deadLetters, isFetching] = useDeadLettersList(filters, isSubscriptionSelected)

  // Reset selected rows when navigating between subscriptions
  useEffect(() => {
    setSelectedIds([])
  }, [subscriptionName])

  // Retrigger mutation
  const [retrigger, isRetriggering, isRetriggerError, resetRetrigger] = useRetriggerDeadLetters()

  // Discard mutation
  const [discard, isDiscarding, isDiscardError, resetDiscard] = useDiscardDeadLetters()

  // Clear mutation
  const [clear, isClearing, isClearError, resetClear] = useClearDeadLetters()

  // Auto-clear success toasts after 5 seconds
  useEffect(() => {
    if (!retriggerSuccess) return
    const timer = setTimeout(() => setRetriggerSuccess(null), 5000)
    return () => clearTimeout(timer)
  }, [retriggerSuccess])

  useEffect(() => {
    if (!discardSuccess) return
    const timer = setTimeout(() => setDiscardSuccess(null), 5000)
    return () => clearTimeout(timer)
  }, [discardSuccess])

  useEffect(() => {
    if (!clearSuccess) return
    const timer = setTimeout(() => setClearSuccess(null), 5000)
    return () => clearTimeout(timer)
  }, [clearSuccess])

  const setPagination = useCallback(
    (model: GridPaginationModel) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('offset', encodeURIComponent(limit * model.page))
      params.set('limit', encodeURIComponent(model.pageSize))
      navigate(`${location.pathname}?${params.toString()}`, { replace: false })
    },
    [searchParams, limit, navigate],
  )

  const handleSelectSubscription = (name: string) => {
    navigate(`${location.pathname}?subscriptionName=${encodeURIComponent(name)}`, { replace: false })
  }

  // Action handlers
  const openDetail = useCallback((dl: DeadLetterLog) => {
    setSelectedDeadLetter(dl)
    setDetailOpen(true)
  }, [])

  const handleRetriggerSingle = useCallback(
    (dl: DeadLetterLog) => {
      setConfirmRetrigger({ type: 'single', deadLetter: dl })
    },
    [],
  )

  const handleRetriggerSelected = useCallback(() => {
    setConfirmRetrigger({ type: 'bulk' })
  }, [])

  const handleConfirmRetrigger = useCallback(async () => {
    if (!confirmRetrigger) return
    setRetriggerSuccess(null)
    resetRetrigger()
    try {
      const ids = confirmRetrigger.type === 'single'
        ? [confirmRetrigger.deadLetter.id]
        : selectedIds.map((id) => String(id))
      const result = await retrigger({ subscriptionName, deadLetterIds: ids })
      setRetriggerSuccess(t('deadLetters.retriggerSuccess', { count: result.retriggeredCount }))
      if (confirmRetrigger.type === 'bulk') setSelectedIds([])
    } catch {
      // error handled by isRetriggerError
    } finally {
      setConfirmRetrigger(null)
    }
  }, [confirmRetrigger, selectedIds, retrigger, t, resetRetrigger, subscriptionName])

  const handleDiscardSelected = useCallback(() => {
    setConfirmDiscard(true)
  }, [])

  const handleConfirmDiscard = useCallback(async () => {
    setDiscardSuccess(null)
    resetDiscard()
    try {
      const ids = selectedIds.map((id) => String(id))
      const result = await discard({ subscriptionName, deadLetterIds: ids })
      setDiscardSuccess(t('deadLetters.discardSuccess', { count: result.discardedCount }))
      setSelectedIds([])
    } catch {
      // error handled by isDiscardError
    } finally {
      setConfirmDiscard(false)
    }
  }, [selectedIds, discard, t, resetDiscard, subscriptionName])

  const handleConfirmClear = useCallback(async () => {
    setClearSuccess(null)
    resetClear()
    try {
      const result = await clear(filters)
      setClearSuccess(t('deadLetters.clearSuccess', { count: result.clearedCount }))
      setSelectedIds([])
    } catch {
      // error handled by isClearError
    } finally {
      setConfirmClear(false)
    }
  }, [clear, filters, t, resetClear])

  const columns: GridColDef<DeadLetterLog>[] = useMemo(
    () => [
      {
        field: 'eventType',
        headerName: t('deadLetters.eventType'),
        flex: 1.5,
        sortable: false,
      },
      {
        field: 'deadLetterReason',
        headerName: t('deadLetters.reason'),
        flex: 1.5,
        sortable: false,
      },
      {
        field: 'status',
        headerName: t('common.status'),
        flex: 0.7,
        sortable: false,
        renderCell: (params) => t(`deadLetterStatus.${params.value}`, params.value as string),
      },
      {
        field: 'eventTime',
        headerName: t('deadLetters.date'),
        flex: 1.5,
        sortable: false,
        renderCell: (params) => formatDateTime(params.row.eventTime ? new Date(params.row.eventTime) : null),
      },
      {
        field: 'deliveryAttempts',
        headerName: t('deadLetters.attempts'),
        flex: 0.5,
        sortable: false,
      },
      {
        field: 'retriggerCount',
        headerName: t('deadLetters.retriggerCount'),
        flex: 0.5,
        sortable: false,
      },
      {
        field: 'actions',
        headerName: t('deadLetters.actions'),
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={t('deadLetters.viewDetail')}>
              <IconButton size="small" onClick={() => openDetail(params.row)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('deadLetters.retrigger')}>
              <IconButton size="small" onClick={() => handleRetriggerSingle(params.row)} disabled={isRetriggering}>
                <BoltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [t, isRetriggering, handleRetriggerSingle, openDetail],
  )

  const localeTextDatagrid =
    i18n.language === 'fr'
      ? frFR.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText

  return (
    <>
      <Paper sx={{ mt: 2, mb: 2, borderRadius: 2, display: 'flex', height: 'calc(100vh - 180px)' }}>
        <SubscriptionSidebar
          selectedSubscription={subscriptionName || null}
          onSelectSubscription={handleSelectSubscription}
        />
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {isSubscriptionSelected ? (
            <>
              <Stack sx={{ px: 2, pt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {subscriptionName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<BoltIcon />}
                      onClick={handleRetriggerSelected}
                      disabled={selectedIds.length === 0 || isRetriggering}
                    >
                      {t('deadLetters.retriggerSelected')} ({selectedIds.length})
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleDiscardSelected}
                      disabled={selectedIds.length === 0 || isDiscarding}
                    >
                      {t('deadLetters.discardSelected')} ({selectedIds.length})
                    </Button>
                    {canClear && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteSweepIcon />}
                        onClick={() => setConfirmClear(true)}
                        disabled={deadLetters.length === 0 || isClearing}
                      >
                        {t('deadLetters.clearList')}
                      </Button>
                    )}
                  </Box>
                </Box>

                {retriggerSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }} onClose={() => setRetriggerSuccess(null)}>
                    {retriggerSuccess}
                  </Alert>
                )}
                {isRetriggerError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {t('deadLetters.retriggerError')}
                  </Alert>
                )}
                {discardSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }} onClose={() => setDiscardSuccess(null)}>
                    {discardSuccess}
                  </Alert>
                )}
                {isDiscardError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {t('deadLetters.discardError')}
                  </Alert>
                )}
                {clearSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }} onClose={() => setClearSuccess(null)}>
                    {clearSuccess}
                  </Alert>
                )}
                {isClearError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {t('deadLetters.clearError')}
                  </Alert>
                )}
              </Stack>

              <DataGrid
                sx={{
                  flexGrow: 1,
                  marginTop: '1rem',
                  border: 'none',
                  '.MuiDataGrid-columnHeaders div:first-of-type': {
                    bgcolor: 'secondary.main',
                    color: 'white',
                    fontWeight: 'bold',
                  },
                  '.MuiDataGrid-cell': {
                    fontWeight: 500,
                  },
                  '.MuiDataGrid-sortIcon': { color: 'white' },
                }}
                rows={deadLetters}
                columns={columns}
                initialState={{
                  pagination: { paginationModel: { pageSize: limit } },
                }}
                onPaginationModelChange={setPagination}
                pageSizeOptions={[25, 50, 100]}
                paginationMode="server"
                rowCount={deadLetters?.length}
                loading={isFetching}
                checkboxSelection
                rowSelectionModel={selectedIds}
                onRowSelectionModelChange={setSelectedIds}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                localeText={localeTextDatagrid}
                slots={{
                  footer: () => (
                    <Footer
                      disabledPreview={offset === 0}
                      disabledNext={deadLetters.length < limit}
                      pagination={pagination}
                      setPagination={setPagination}
                    />
                  ),
                }}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">{t('deadLetters.selectSubscription')}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialogs */}
      <DeadLetterDetailDialog
        deadLetter={selectedDeadLetter}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false)
          setSelectedDeadLetter(null)
        }}
      />

      <Dialog open={!!confirmRetrigger} onClose={() => setConfirmRetrigger(null)}>
        <DialogTitle>{t('deadLetters.confirmRetriggerTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmRetrigger?.type === 'single'
              ? t('deadLetters.confirmRetriggerSingle')
              : t('deadLetters.confirmRetriggerBulk', { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRetrigger(null)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmRetrigger} variant="contained" disabled={isRetriggering}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>{t('deadLetters.confirmDiscardTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deadLetters.confirmDiscardBulk', { count: selectedIds.length })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDiscard(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDiscard} variant="contained" color="error" disabled={isDiscarding}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>{t('deadLetters.confirmClearTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deadLetters.confirmClear')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmClear} variant="contained" color="error" disabled={isClearing}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DeadLettersPage
